import { GameScene } from '../GameScene';
import { SystemConsole } from '../ui/SystemConsole';
import type { GameStateView, PlayerStateView, SchemaMap } from '../../state/GameStateView';
import { ClientMessageType, type ChatMessage } from '@shared/types/messages';

/**
 * UI de chat + consola del sistema: crea la caja de input de chat (createChatUI) con su parser de
 * comandos "/", refresca el texto tipeado con cursor parpadeante (updateChatInputText), crea la
 * SystemConsole (createSystemConsoleUI) y vuelca mensajes de jugador/sistema a ella (addChatMessage /
 * addChatConsoleMessage / addSystemMessage). Extraído de GameScene (god-object). El estado del chat
 * (chatInputText/chatCursor/chatIsFocused…) y la instancia systemConsole viven en GameScene (host) y
 * se acceden vía `this.scene.*`. Ver docs/CHAT_AND_COMMANDS.md.
 */
export class ChatUiController {
  constructor(private scene: GameScene) {}

  public createChatUI(): void {
    const { width } = this.scene.scale;

    // Ya no usamos un cuadro de chat separado abajo a la izquierda.

    // El input de chat se integra en la consola de sistema superior.

    this.scene.chatBackground = null;

    // Texto de input (posición inicial; se recoloca dentro de la consola en layoutSystemConsole)

    this.scene.chatInputText = this.scene.addText(14, 14, '', {
        fontSize: '16px',

        color: '#ffffaa', // Color más claro (amarillo claro)
      })

      .setOrigin(0, 1)

      .setDepth(801)

      .setScrollFactor(0);

    // Cursor parpadeante para el input

    this.scene.chatCursor = this.scene.addText(14, 14, '|', {
        fontSize: '14px',

        color: '#ffffaa',
      })

      .setOrigin(0, 1)

      .setDepth(802)

      .setScrollFactor(0)

      .setVisible(false);

    // Manejo de teclado para chat

    this.scene.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (!this.scene.colyseusClient) return;

      if (!this.scene.chatIsFocused) {
        if (event.key === 'Enter') {
          // Entrar en modo chat - permitir que la caminata actual termine naturalmente

          this.scene.chatIsFocused = true;

          this.scene.chatCurrentInput = '';

          // Enviar STOP_MOVE para detener el movimiento en el servidor

          // Esto evita que lleguen nuevos updates de movimiento, pero permite

          // que la interpolación actual termine naturalmente

          this.scene.inputSystem?.resetMovement();

          // NO hacer snap inmediato - dejar que la caminata actual termine naturalmente

          // La interpolación continuará hasta completarse, y luego no habrá nuevas caminatas

          // porque chatIsFocused está activo y bloquea el procesamiento de movimiento

          // Asegurar que el cursor se muestre inmediatamente

          if (this.scene.chatCursor) {
            this.scene.chatCursorBlinkTimer = 0;

            this.scene.chatCursor.setVisible(true);
          }

          this.updateChatInputText();

          event.preventDefault();
        }

        return;
      }

      // Si estamos en modo chat

      if (event.key === 'Enter') {
        const text = this.scene.chatCurrentInput.trim();

        if (text.length > 0) {
          // Comandos con prefijo "/"

          if (text.startsWith('/')) {
            const [command] = text.split(/\s+/);

            const cmd = command.toLowerCase();

            if (cmd === '/invocar') {
              this.scene.devPanels.openInvokePanel();
            } else if (cmd === '/items') {
              this.scene.itemPanelCtl.openItemPanel();
            } else if (cmd === '/exp') {
              // Dev command: send to server as chat (server parses /exp)
              this.scene.colyseusClient.send({ type: ClientMessageType.CHAT, message: text } as any);
            } else if (cmd === '/party' || cmd === '/grupo') {
              // Abre/cierra la ventana de party. Ver docs/PARTY_SYSTEM.md.
              this.scene.partyController?.togglePanel();
            } else if (cmd === '/invitar' || cmd === '/invite') {
              const name = text.split(/\s+/).slice(1).join(' ').trim();
              if (!name) {
                this.addSystemMessage('Uso: /invitar <nombre>');
              } else {
                // Resolver el sessionId del jugador por nombre en el room state.
                let targetSid: string | null = null;
                this.scene.room?.state.players.forEach((p, sid) => {
                  if ((p.name ?? '').toLowerCase() === name.toLowerCase()) targetSid = sid;
                });
                if (targetSid) this.scene.partyController?.invite(targetSid);
                else this.addSystemMessage(`No encuentro a "${name}" cerca.`);
              }
            } else {
              this.addSystemMessage(`Comando desconocido: ${text}`);
            }
          } else {
            this.scene.colyseusClient.send({
              type: ClientMessageType.CHAT,

              message: text,
            } as ChatMessage);
          }
        }

        this.scene.chatIsFocused = false;

        this.scene.chatCurrentInput = '';

        this.updateChatInputText();

        event.preventDefault();
      } else if (event.key === 'Escape') {
        // Salir del modo chat sin enviar

        this.scene.chatIsFocused = false;

        this.scene.chatCurrentInput = '';

        this.updateChatInputText();

        event.preventDefault();
      } else if (event.key === 'Backspace') {
        this.scene.chatCurrentInput = this.scene.chatCurrentInput.slice(0, -1);

        this.updateChatInputText();

        event.preventDefault();
      } else if (event.key.length === 1) {
        // Carácter imprimible

        this.scene.chatCurrentInput += event.key;

        this.updateChatInputText();

        event.preventDefault();
      }
    });
  }

  public updateChatInputText(): void {
    if (!this.scene.chatInputText || !this.scene.chatCursor) return;

    if (!this.scene.chatIsFocused) {
      this.scene.chatInputText.setText(''); // No mostramos input si no está enfocado

      this.scene.chatCursor.setVisible(false);

      this.scene.chatCursorBlinkTimer = 0;
    } else {
      // Flecha > más clara y texto más claro

      this.scene.chatInputText.setText(`> ${this.scene.chatCurrentInput}`);

      // Resetear timer de parpadeo cuando se actualiza el texto

      this.scene.chatCursorBlinkTimer = 0;

      // Asegurar que el cursor sea visible inicialmente

      this.scene.chatCursor.setVisible(true);

      // Posicionar cursor después del texto (se actualizará en layoutSystemConsole)

      this.scene.layout.layoutSystemConsole();
    }
  }

  public addChatMessage(playerName: string, message: string): void {
    // Todos los mensajes de chat (incluyendo entidades) van a la consola de sistema superior.

    // Para mensajes del servidor, evitamos duplicar el prefijo de nombre.

    if (playerName === 'Servidor') {
      this.addSystemMessage(message);
    } else {
      this.addChatConsoleMessage(playerName, message);
    }

    // Crear / actualizar globo de chat sobre la cabeza del jugador que habla

    // (incluye también al jugador local, usando el eco del servidor)

    const state = this.scene.room?.state as unknown as GameStateView | undefined;

    const players = state?.players as SchemaMap<PlayerStateView> | undefined;

    if (!players) return;

    // Buscar el playerId por nombre (en el futuro podemos usar directamente playerId del mensaje)

    let playerId: string | null = null;

    players.forEach((p, id) => {
      if (!playerId && p.name === playerName) {
        playerId = id;
      }
    });

    if (playerId) {
      this.scene.overheadText.showChatBubble(playerId, message);
    }
  }



  public createSystemConsoleUI(): void {
    const saved = this.scene.persistence.loadClientOptions();
    const { width, height } = this.scene.scale;

    const initialX = saved?.systemConsoleX ?? 10;
    const initialY = saved?.systemConsoleY ?? 10;
    const initialWidth = Math.min(saved?.systemConsoleWidth ?? 464, Math.max(260, width - 20));
    const initialHeight = saved?.systemConsoleHeight ?? 110;

    this.scene.systemConsole = new SystemConsole({
      scene: this.scene,
      initialX,
      initialY,
      initialWidth,
      initialHeight,
      minWidth: 260,
      minHeight: 60,
      fontFamily: this.scene.getSelectedPlayerNamesFont(),
      chatInputText: this.scene.chatInputText,
      chatCursor: this.scene.chatCursor,
      getChatIsFocused: () => this.scene.chatIsFocused,
      onPositionSizeChange: (x, y, w, h) => this.scene.persistence.saveConsolePosition(x, y, w, h),
    });
  }




  public addSystemMessage(text: string, color?: string): void {
    this.scene.systemConsole?.addSystemMessage(text, color);
  }

  public addChatConsoleMessage(playerName: string, message: string): void {
    this.scene.systemConsole?.addChatMessage(playerName, message);
  }
}
